import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowRightIcon,
    ArrowLeftIcon,
    UserIcon,
    BuildingOfficeIcon,
    CalendarIcon,
    CheckIcon,
    PlusIcon,
    TrashIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    BriefcaseIcon,
    XMarkIcon,
    FolderIcon,
    CheckCircleIcon,
    MagnifyingGlassIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import AddTaskModal, { AddTaskData } from '../../components/checklist/AddTaskModal';
import {
    OffboardingRequestsClient,
    OnboardingRequestsClient,
    OffboardingPreviewDto,
    PreviewTaskDto,
    OffboardingAction,
    OffboardingReason,
    TaskPhase,
    TaskSource,
    TemplateCategoriesClient,
    TemplateCategoryListDto,
    TemplatesClient,
    TemplateDto,
    EmployeesClient,
    EmployeeDto,
    RequestStatus,
    RequestType,
    TeamDto
} from '../../web-api-client';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import { usersService, User } from '../../services/usersService';
import { teamsService } from '../../services/teamsService';
import { offboardingReasonOptions } from '../../utils/enumHelpers';

interface NewOffboardingRequestFormProps {
    show: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** Pre-selected employee ID (skips step 1) */
    initialEmployeeId?: number;
    /** Pre-filled remarks for the offboarding */
    initialRemarks?: string;
}

export default function NewOffboardingRequestForm({ show, onClose, onSuccess, initialEmployeeId, initialRemarks }: NewOffboardingRequestFormProps) {
    const [currentStep, setCurrentStep] = useState(1);

    // Step 1 - Employee Selection
    const [employees, setEmployees] = useState<EmployeeDto[]>([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
    const [excludedEmployeeIds, setExcludedEmployeeIds] = useState<Set<number>>(new Set());

    // Step 2 - Template Selection
    const [templates, setTemplates] = useState<TemplateDto[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // Step 3 & 4 - Preview and Tasks
    const [preview, setPreview] = useState<OffboardingPreviewDto | null>(null);
    const [tasks, setTasks] = useState<PreviewTaskDto[]>([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);

    // Step 2 state - Offboarding Details
    const [lastWorkingDate, setLastWorkingDate] = useState<string>('');
    const [offboardingReason, setOffboardingReason] = useState<OffboardingReason | ''>('');
    const [remarks, setRemarks] = useState('');

    // Step 3 state - Task Selection
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
    const [categories, setCategories] = useState<TemplateCategoryListDto[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [teams, setTeams] = useState<TeamDto[]>([]);

    // Add task modal state - now using AddTaskModal component

    // Form errors
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

    // Display limit for employee list (to avoid infinite scrolling with 100+ users)
    const EMPLOYEE_DISPLAY_LIMIT = 20;

    // Filtered employees based on search and exclude those with active/completed offboarding
    const filteredEmployees = useMemo(() => {
        // First filter out employees with active/completed offboarding
        let filtered = employees.filter(e => !excludedEmployeeIds.has(e.id!));

        // Then apply search filter
        if (employeeSearch.trim()) {
            const search = employeeSearch.toLowerCase();
            filtered = filtered.filter(e =>
                e.fullName?.toLowerCase().includes(search) ||
                e.employeeNumber?.toLowerCase().includes(search) ||
                e.designation?.toLowerCase().includes(search)
            );
        }

        return filtered;
    }, [employees, employeeSearch, excludedEmployeeIds]);

    // Displayed employees - limit to EMPLOYEE_DISPLAY_LIMIT when not searching
    const displayedEmployees = useMemo(() => {
        const isSearching = employeeSearch.trim().length > 0;
        // When searching, show all matches; otherwise limit to prevent infinite scroll
        return isSearching ? filteredEmployees : filteredEmployees.slice(0, EMPLOYEE_DISPLAY_LIMIT);
    }, [filteredEmployees, employeeSearch]);

    // Check if there are more employees than displayed
    const hasMoreEmployees = filteredEmployees.length > displayedEmployees.length;

    // Fetch employees when modal opens (Step 1)
    useEffect(() => {
        const fetchEmployees = async () => {
            if (!show) return;

            try {
                setLoadingEmployees(true);

                // Fetch employees, onboarding requests, and offboarding requests
                // Only get active, non-draft employees for offboarding
                const [employeesData, onboardingRequests, offboardingRequests] = await Promise.all([
                    new EmployeesClient(API_CONFIG.baseUrl, createAuthenticatedFetch()).getEmployees(undefined, true, false),
                    new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch()).getOnboardingRequests(),
                    new OffboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch()).getOffboardingRequests()
                ]);

                // Get employee IDs with COMPLETED onboarding (only these can be offboarded)
                const completedOnboardingEmployeeIds = new Set<number>();
                onboardingRequests.forEach(req => {
                    const status = Number(req.status);
                    if (status === RequestStatus.Completed && req.employeeId) {
                        completedOnboardingEmployeeIds.add(req.employeeId);
                    }
                });

                // Get employee IDs that have active offboarding (exclude cancelled/rejected)
                const hasActiveOffboardingIds = new Set<number>();
                offboardingRequests.forEach(req => {
                    const status = Number(req.status);
                    // Exclude employees with Draft, Pending, InProgress, Approved, or Completed offboarding
                    if (status !== RequestStatus.Cancelled && status !== RequestStatus.Rejected) {
                        if (req.employeeId) {
                            hasActiveOffboardingIds.add(req.employeeId);
                        }
                    }
                });

                // Only show employees with completed onboarding and no active offboarding
                const eligibleEmployees = employeesData.filter(emp =>
                    completedOnboardingEmployeeIds.has(emp.id!) && !hasActiveOffboardingIds.has(emp.id!)
                );

                setExcludedEmployeeIds(hasActiveOffboardingIds);
                setEmployees(eligibleEmployees);
            } catch (error) {
                console.error('Failed to fetch employees:', error);
                toast.error('Failed to load employees');
            } finally {
                setLoadingEmployees(false);
            }
        };

        fetchEmployees();
    }, [show]);

    // Fetch categories, users, teams for add task modal
    useEffect(() => {
        const fetchData = async () => {
            if (!show) return;
            try {
                const [categoriesData, usersData, teamsData] = await Promise.all([
                    new TemplateCategoriesClient(API_CONFIG.baseUrl, createAuthenticatedFetch()).getTemplateCategories(true),
                    usersService.getUsers(),
                    teamsService.getTeams()
                ]);
                setCategories(categoriesData);
                setUsers(usersData);
                setTeams(teamsData);
            } catch (error) {
                console.error('Failed to fetch reference data:', error);
            }
        };
        fetchData();
    }, [show]);

    // Fetch templates when step 2 is reached
    useEffect(() => {
        const fetchTemplates = async () => {
            if (currentStep === 2) {
                try {
                    setLoadingTemplates(true);
                    const client = new TemplatesClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
                    const data = await client.getTemplates(RequestType.Offboarding);
                    setTemplates(data);
                } catch (error) {
                    console.error('Failed to fetch templates:', error);
                    toast.error('Failed to load templates');
                } finally {
                    setLoadingTemplates(false);
                }
            }
        };
        fetchTemplates();
    }, [currentStep]);

    // Reset form when closed
    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show]);

    // Handle pre-selected employee (skip to step 2)
    useEffect(() => {
        const initializeWithEmployee = async () => {
            if (show && initialEmployeeId) {
                try {
                    setLoadingPreview(true);
                    setSelectedEmployeeId(initialEmployeeId);

                    const client = new OffboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
                    const data = await client.initializeOffboardingPreview(initialEmployeeId);
                    setPreview(data);
                    setTasks(data.suggestedTasks || []);

                    // Set default last working date to today + 30 days
                    const defaultDate = new Date();
                    defaultDate.setDate(defaultDate.getDate() + 30);
                    setLastWorkingDate(defaultDate.toISOString().split('T')[0]);

                    // Set initial remarks if provided
                    if (initialRemarks) {
                        setRemarks(initialRemarks);
                    }

                    // Skip to step 2
                    setCurrentStep(2);
                } catch (error) {
                    console.error('Failed to fetch offboarding preview:', error);
                    toast.error('Failed to load offboarding preview');
                } finally {
                    setLoadingPreview(false);
                }
            }
        };

        initializeWithEmployee();
    }, [show, initialEmployeeId, initialRemarks]);

    const resetForm = () => {
        setCurrentStep(1);
        setEmployees([]);
        setEmployeeSearch('');
        setSelectedEmployeeId(null);
        setExcludedEmployeeIds(new Set());
        setTemplates([]);
        setSelectedTemplateId(null);
        setPreview(null);
        setTasks([]);
        setLastWorkingDate('');
        setOffboardingReason('');
        setRemarks('');
        setCollapsedCategories(new Set());
        setFormErrors({});
    };

    // Handle employee selection (just highlight, don't move to Step 2 yet)
    const handleSelectEmployee = (employeeId: number) => {
        setSelectedEmployeeId(employeeId);
    };

    // Handle Step 1 Next button - move to template selection
    const handleStep1Next = () => {
        if (!selectedEmployeeId) {
            toast.error('Please select an employee');
            return;
        }
        setCurrentStep(2);
    };

    // Handle Step 2 (template selection) - move to offboarding details
    const handleStep2Next = () => {
        // Template is optional, just move to next step
        // Set default last working date to today + 30 days
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 30);
        setLastWorkingDate(defaultDate.toISOString().split('T')[0]);
        setCurrentStep(3);
    };

    // Group tasks by category
    const tasksByCategory = useMemo(() => {
        const groups: { [key: string]: { categoryId: number | null; categoryName: string; tasks: PreviewTaskDto[] } } = {};

        tasks.forEach((task, index) => {
            const categoryKey = task.templateCategoryId?.toString() || 'uncategorized';
            const categoryName = task.categoryName || 'Uncategorized';

            if (!groups[categoryKey]) {
                groups[categoryKey] = {
                    categoryId: task.templateCategoryId || null,
                    categoryName,
                    tasks: []
                };
            }

            groups[categoryKey].tasks.push({ ...task, displayOrder: index } as PreviewTaskDto);
        });

        return Object.values(groups).sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    }, [tasks]);

    const toggleCategory = (categoryName: string) => {
        setCollapsedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryName)) {
                newSet.delete(categoryName);
            } else {
                newSet.add(categoryName);
            }
            return newSet;
        });
    };

    const handleToggleTask = (index: number) => {
        setTasks(prevTasks => prevTasks.map((task, i) =>
            i === index ? new PreviewTaskDto({ ...task, isSelected: !task.isSelected }) : task
        ));
    };

    const handleToggleAll = () => {
        setTasks(prevTasks => {
            const allSelected = prevTasks.every(t => t.isSelected);
            return prevTasks.map(task => new PreviewTaskDto({ ...task, isSelected: !allSelected }));
        });
    };

    const handleRemoveTask = (index: number) => {
        setTasks(prevTasks => prevTasks.filter((_, i) => i !== index));
        toast.success('Task removed');
    };

    // Handle Step 3 (offboarding details) - fetch preview with template and move to task selection
    const handleStep3Submit = async () => {
        // Validate Step 3 fields
        const errors: { [key: string]: string } = {};

        if (!lastWorkingDate) {
            errors.lastWorkingDate = 'Please select a last working date';
        }
        if (!offboardingReason) {
            errors.offboardingReason = 'Please select an offboarding reason';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            toast.error('Please fill in all required fields');
            return;
        }

        setFormErrors({});

        // Fetch preview with template support
        try {
            setLoadingPreview(true);
            const client = new OffboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());

            // Use the appropriate endpoint based on whether a template is selected
            const data = selectedTemplateId
                ? await client.initializeOffboardingPreviewWithTemplate(selectedEmployeeId!, selectedTemplateId)
                : await client.initializeOffboardingPreview(selectedEmployeeId!);

            setPreview(data);
            setTasks(data.suggestedTasks || []);
            setCurrentStep(4);
        } catch (error) {
            console.error('Failed to fetch offboarding preview:', error);
            toast.error('Failed to load offboarding preview');
        } finally {
            setLoadingPreview(false);
        }
    };

    // Save as draft only
    const handleSaveAsDraft = async () => {
        if (!preview) return;

        const selectedTasks = tasks.filter(t => t.isSelected);
        if (selectedTasks.length === 0) {
            toast.error('Please select at least one task');
            return;
        }

        try {
            setGenerating(true);

            const command = {
                employeeId: preview.employeeId,
                lastWorkingDate: new Date(lastWorkingDate).toISOString(),
                reason: offboardingReason,
                remarks: remarks || undefined,
                selectedTasks: selectedTasks.map((task, index) => ({
                    itemName: task.itemName,
                    description: task.description,
                    assignedAsset: task.assignedAsset,
                    picId: task.picId,
                    teamId: task.teamId,
                    templateCategoryId: task.templateCategoryId,
                    offboardingAction: task.defaultAction,
                    extendedUntil: undefined,
                    remarks: undefined,
                    sourceTaskId: task.sourceTaskId,
                    displayOrder: index + 1
                }))
            };

            const response = await createAuthenticatedFetch().fetch(`${API_CONFIG.baseUrl}/api/offboardingrequests/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(command)
            });

            if (!response.ok) {
                throw new Error('Failed to save offboarding draft');
            }

            toast.success('Offboarding saved as draft. Open it to review and submit.');
            onClose();
            onSuccess();
        } catch (error) {
            console.error('Failed to save offboarding draft:', error);
            toast.error('Failed to save offboarding draft');
        } finally {
            setGenerating(false);
        }
    };

    // Create and immediately submit (notify PICs)
    const handleCreateAndSubmit = async () => {
        if (!preview) return;

        const selectedTasks = tasks.filter(t => t.isSelected);
        if (selectedTasks.length === 0) {
            toast.error('Please select at least one task');
            return;
        }

        try {
            setGenerating(true);

            const command = {
                employeeId: preview.employeeId,
                lastWorkingDate: new Date(lastWorkingDate).toISOString(),
                reason: offboardingReason,
                remarks: remarks || undefined,
                selectedTasks: selectedTasks.map((task, index) => ({
                    itemName: task.itemName,
                    description: task.description,
                    assignedAsset: task.assignedAsset,
                    picId: task.picId,
                    teamId: task.teamId,
                    templateCategoryId: task.templateCategoryId,
                    offboardingAction: task.defaultAction,
                    extendedUntil: undefined,
                    remarks: undefined,
                    sourceTaskId: task.sourceTaskId,
                    displayOrder: index + 1
                }))
            };

            // Use the combined create-and-submit endpoint
            const response = await createAuthenticatedFetch().fetch(`${API_CONFIG.baseUrl}/api/offboardingrequests/generate-and-submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(command)
            });

            if (!response.ok) {
                throw new Error('Failed to create and submit offboarding request');
            }

            toast.success('Offboarding created and submitted. Notifications sent to PICs.');
            onClose();
            onSuccess();
        } catch (error) {
            console.error('Failed to create offboarding request:', error);
            toast.error('Failed to create offboarding request');
        } finally {
            setGenerating(false);
        }
    };

    const handleAddTask = async (taskData: AddTaskData): Promise<void> => {
        const newTask = new PreviewTaskDto({
            itemName: taskData.taskName || '',
            description: taskData.description || undefined,
            templateCategoryId: taskData.categoryId || undefined,
            categoryName: taskData.categoryName || 'Uncategorized',
            defaultAction: OffboardingAction.Outstanding,
            sourcePhase: TaskPhase.Onboarding,
            sourceTaskId: 0,
            isSelected: true,
            displayOrder: tasks.length + 1
        });

        setTasks([...tasks, newTask]);
        setShowAddTaskModal(false);
    };

    const getPhaseBadge = (phase: TaskPhase) => {
        if (phase === TaskPhase.PostOnboarding) {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                    Post-Onboarding
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                Onboarding
            </span>
        );
    };

    const getSourceBadge = (source: TaskSource) => {
        switch (source) {
            case TaskSource.Template:
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                        Template
                    </span>
                );
            case TaskSource.Onboarding:
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                        Onboarding
                    </span>
                );
            case TaskSource.Both:
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 rounded-full">
                        Merged
                    </span>
                );
            case TaskSource.Manual:
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                        Manual
                    </span>
                );
            default:
                return null;
        }
    };

    const activeTasks = tasks.filter(t => !t.isAlreadyReturned);
    const alreadyReturnedCount = tasks.filter(t => t.isAlreadyReturned).length;
    const selectedCount = activeTasks.filter(t => t.isSelected).length;

    // Get selected employee from the employees list
    const selectedEmployee = useMemo(() => {
        return employees.find(e => e.id === selectedEmployeeId);
    }, [employees, selectedEmployeeId]);

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    const handleBack = () => {
        if (currentStep === 2) {
            // Going back to Step 1, keep employee selection
            setCurrentStep(1);
        } else if (currentStep === 3) {
            // Going back to Step 2, keep template selection
            setCurrentStep(2);
        } else if (currentStep === 4) {
            // Going back to Step 3, reset preview
            setPreview(null);
            setTasks([]);
            setCurrentStep(3);
        }
    };

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full p-6 relative z-50 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">New Offboarding Request</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Step {currentStep} of 4</p>
                    </div>
                    <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Progress Indicator - 4 Steps */}
                <div className="flex items-center mb-8">
                    <div className={`flex-1 h-2 rounded-full ${currentStep >= 1 ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
                    <div className={`flex-1 h-2 rounded-full ml-2 ${currentStep >= 2 ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
                    <div className={`flex-1 h-2 rounded-full ml-2 ${currentStep >= 3 ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
                    <div className={`flex-1 h-2 rounded-full ml-2 ${currentStep >= 4 ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
                </div>

                {/* Step 1: Select Employee */}
                {currentStep === 1 && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Employee for Offboarding</h3>

                        {/* Search Input */}
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, employee number, or designation..."
                                value={employeeSearch}
                                onChange={(e) => setEmployeeSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                autoFocus
                            />
                        </div>

                        {/* Employee Count Info */}
                        {!loadingEmployees && filteredEmployees.length > 0 && (
                            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 px-1">
                                <span>
                                    {hasMoreEmployees ? (
                                        <>Showing {displayedEmployees.length} of {filteredEmployees.length} employees</>
                                    ) : (
                                        <>{filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} found</>
                                    )}
                                </span>
                                {hasMoreEmployees && (
                                    <span className="text-red-600 dark:text-red-400 font-medium">
                                        Type to search for more
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Employee List */}
                        <div className="max-h-[50vh] overflow-y-auto">
                            {loadingEmployees ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="text-gray-500 dark:text-gray-400">Loading employees...</div>
                                </div>
                            ) : displayedEmployees.length === 0 ? (
                                <div className="text-center py-8">
                                    <UserIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {employeeSearch ? 'No employees found matching your search' : 'No employees available'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {displayedEmployees.map((employee) => {
                                        const isSelected = selectedEmployeeId === employee.id;
                                        return (
                                            <button
                                                key={employee.id}
                                                onClick={() => handleSelectEmployee(employee.id!)}
                                                className={`w-full flex items-center p-4 rounded-lg border-2 transition-all text-left group ${
                                                    isSelected
                                                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                                        : 'border-gray-200 dark:border-gray-700 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                }`}
                                            >
                                                <div className="flex-1">
                                                    <div className={`font-semibold ${
                                                        isSelected
                                                            ? 'text-red-700 dark:text-red-400'
                                                            : 'text-gray-900 dark:text-white group-hover:text-red-700 dark:group-hover:text-red-400'
                                                    }`}>
                                                        {employee.fullName}
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                        {employee.employeeNumber && (
                                                            <span className="flex items-center gap-1">
                                                                <UserIcon className="h-4 w-4" />
                                                                {employee.employeeNumber}
                                                            </span>
                                                        )}
                                                        {employee.designation && (
                                                            <span>{employee.designation}</span>
                                                        )}
                                                        {employee.department && (
                                                            <span className="flex items-center gap-1">
                                                                <BuildingOfficeIcon className="h-4 w-4" />
                                                                {employee.department}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {isSelected ? (
                                                    <CheckCircleIcon className="h-6 w-6 text-red-600" />
                                                ) : (
                                                    <div className="h-6 w-6 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Step 1 Actions */}
                        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleStep1Next}
                                disabled={!selectedEmployeeId || loadingPreview}
                                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                            >
                                {loadingPreview ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Next
                                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Select Template (Optional) */}
                {currentStep === 2 && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Select Offboarding Template (Optional)
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Choose a template to include additional offboarding tasks, or skip to use only tasks from the employee's onboarding.
                        </p>

                        {loadingTemplates ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full"></div>
                            </div>
                        ) : templates.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                <p className="text-gray-500 dark:text-gray-400">No offboarding templates available.</p>
                                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                                    You can continue without a template - tasks will be loaded from the employee's onboarding.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[50vh] overflow-y-auto">
                                {templates.map((template) => (
                                    <button
                                        key={template.id}
                                        onClick={() => setSelectedTemplateId(
                                            selectedTemplateId === template.id ? null : template.id!
                                        )}
                                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all text-left ${
                                            selectedTemplateId === template.id
                                                ? 'border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900 dark:text-white">
                                                    {template.templateName}
                                                </h4>
                                                {template.description && (
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {template.description}
                                                    </p>
                                                )}
                                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                                                    {template.categories?.reduce(
                                                        (total, category) => total + (category.tasks?.length || 0), 0
                                                    ) || 0} tasks
                                                </p>
                                            </div>
                                            {selectedTemplateId === template.id && (
                                                <CheckCircleIcon className="h-6 w-6 text-red-600 flex-shrink-0 ml-2" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Step 2 Actions */}
                        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 inline-flex items-center"
                            >
                                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={handleStep2Next}
                                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 inline-flex items-center"
                            >
                                {selectedTemplateId ? 'Next' : 'Skip Template'}
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Offboarding Details */}
                {currentStep === 3 && selectedEmployee && (
                    <div className="space-y-6">
                        {/* Employee Information (Read-only) */}
                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                <UserIcon className="h-5 w-5 mr-2 text-red-600" />
                                Employee Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div className="flex items-start">
                                    <UserIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                                    <div>
                                        <span className="text-gray-600 dark:text-gray-400">Full Name:</span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedEmployee.fullName}</span>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <BriefcaseIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                                    <div>
                                        <span className="text-gray-600 dark:text-gray-400">Designation:</span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedEmployee.designation || '-'}</span>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                                    <div>
                                        <span className="text-gray-600 dark:text-gray-400">Location:</span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedEmployee.location || '-'}</span>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                                    <div>
                                        <span className="text-gray-600 dark:text-gray-400">Department:</span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedEmployee.department || '-'}</span>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                                    <div>
                                        <span className="text-gray-600 dark:text-gray-400">Employment Date:</span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                            {selectedEmployee.employmentDate ? new Date(selectedEmployee.employmentDate + 'Z').toLocaleDateString() : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Offboarding Details Form */}
                        <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                <CalendarIcon className="h-5 w-5 mr-2 text-red-600" />
                                Offboarding Details
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Last Working Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={lastWorkingDate}
                                        onChange={(e) => {
                                            setLastWorkingDate(e.target.value);
                                            if (formErrors.lastWorkingDate) {
                                                setFormErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.lastWorkingDate;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        min={new Date().toISOString().split('T')[0]}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                                            formErrors.lastWorkingDate
                                                ? 'border-red-500 focus:ring-red-500'
                                                : 'border-gray-300 dark:border-gray-600 focus:ring-red-500'
                                        }`}
                                    />
                                    {formErrors.lastWorkingDate && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.lastWorkingDate}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Offboarding Reason <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={offboardingReason}
                                        onChange={(e) => {
                                            setOffboardingReason(e.target.value ? parseInt(e.target.value) as OffboardingReason : '');
                                            if (formErrors.offboardingReason) {
                                                setFormErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.offboardingReason;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                                            formErrors.offboardingReason
                                                ? 'border-red-500 focus:ring-red-500'
                                                : 'border-gray-300 dark:border-gray-600 focus:ring-red-500'
                                        }`}
                                    >
                                        <option value="">Select reason...</option>
                                        {offboardingReasonOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {formErrors.offboardingReason && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.offboardingReason}</p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Remarks (Optional)
                                    </label>
                                    <textarea
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        placeholder="Additional notes..."
                                        rows={3}
                                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Step 3 Actions */}
                        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 inline-flex items-center"
                            >
                                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={handleStep3Submit}
                                disabled={loadingPreview}
                                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                            >
                                {loadingPreview ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Next
                                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 4: Task Selection */}
                {currentStep === 4 && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Select Offboarding Tasks
                            </h3>
                            <button
                                onClick={() => setShowAddTaskModal(true)}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Add Task
                            </button>
                        </div>

                        {/* Summary Stats */}
                        <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-lg p-3">
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">Total Items</div>
                                    <div className="text-lg font-bold text-gray-900 dark:text-white">{tasks.length}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">Selected</div>
                                    <div className="text-lg font-bold text-blue-600">{selectedCount}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Already Returned</div>
                                    <div className="text-lg font-bold text-gray-500">{alreadyReturnedCount}</div>
                                </div>
                            </div>
                        </div>

                        {/* Task List - Grouped by Category */}
                        {tasks.length > 0 ? (
                            <div className="space-y-4 max-h-[40vh] overflow-y-auto">
                                {/* Select All */}
                                <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                    <input
                                        type="checkbox"
                                        checked={tasks.every(t => t.isSelected)}
                                        onChange={handleToggleAll}
                                        className="h-5 w-5 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                    />
                                    <label className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                                        Select All ({tasks.length} tasks)
                                    </label>
                                </div>

                                {/* Category Groups */}
                                {tasksByCategory.map((group) => {
                                    const isCollapsed = collapsedCategories.has(group.categoryName);
                                    const selectedInCategory = group.tasks.filter(t => t.isSelected).length;

                                    return (
                                        <div key={group.categoryName} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                                            {/* Category Header */}
                                            <button
                                                onClick={() => toggleCategory(group.categoryName)}
                                                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 hover:from-gray-100 hover:to-gray-150 dark:hover:from-gray-700 dark:hover:to-gray-800 transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <FolderIcon className="h-5 w-5 text-red-600" />
                                                    <span className="font-semibold text-gray-900 dark:text-white">{group.categoryName}</span>
                                                    <span className="px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                                                        {selectedInCategory}/{group.tasks.length} selected
                                                    </span>
                                                </div>
                                                {isCollapsed ? (
                                                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                                                ) : (
                                                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                                                )}
                                            </button>

                                            {/* Category Tasks */}
                                            {!isCollapsed && (
                                                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                    {group.tasks.map((task) => {
                                                        const taskIndex = tasks.findIndex(t =>
                                                            t.itemName === task.itemName &&
                                                            t.sourceTaskId === task.sourceTaskId
                                                        );
                                                        const isReturned = task.isAlreadyReturned;

                                                        return (
                                                            <div
                                                                key={`${task.sourceTaskId}-${task.itemName}`}
                                                                className={`p-4 transition-all ${
                                                                    isReturned
                                                                        ? 'bg-green-50 dark:bg-green-900/20 opacity-75'
                                                                        : task.isSelected
                                                                            ? 'bg-red-50 dark:bg-red-900/20'
                                                                            : 'bg-white dark:bg-gray-800'
                                                                }`}
                                                            >
                                                                <div className="flex items-start gap-4">
                                                                    {isReturned ? (
                                                                        <div className="mt-1 h-5 w-5 flex items-center justify-center">
                                                                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                                                        </div>
                                                                    ) : (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={task.isSelected}
                                                                            onChange={() => handleToggleTask(taskIndex)}
                                                                            className="mt-1 h-5 w-5 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                                                        />
                                                                    )}

                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-start justify-between gap-4">
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-center gap-2">
                                                                                    <h4 className={`text-base font-medium ${isReturned ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                                                                                        {task.itemName}
                                                                                    </h4>
                                                                                    {isReturned && (
                                                                                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400 rounded-full">
                                                                                            Already Returned
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                {task.description && (
                                                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{task.description}</p>
                                                                                )}
                                                                                {/* Show returned date and remarks */}
                                                                                {isReturned && (
                                                                                    <div className="mt-2 text-xs text-green-700 dark:text-green-400 space-y-1">
                                                                                        {task.completedDate && (
                                                                                            <div className="flex items-center gap-1">
                                                                                                <CalendarIcon className="h-3.5 w-3.5" />
                                                                                                <span>Returned on: {new Date(task.completedDate).toLocaleDateString()}</span>
                                                                                            </div>
                                                                                        )}
                                                                                        {task.remarks && (
                                                                                            <div className="italic">Note: {task.remarks}</div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            {/* Removed source/phase badges - not needed for offboarding checklist */}
                                                                        </div>

                                                                        <div className="flex flex-wrap items-center gap-3 mt-3">
                                                                            {task.assignedAsset && (
                                                                                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded ${
                                                                                    isReturned
                                                                                        ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 line-through'
                                                                                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                                                                }`}>
                                                                                    {task.assignedAsset}
                                                                                </span>
                                                                            )}
                                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                                {task.teamName ? `Team: ${task.teamName}` :
                                                                                 task.picName ? `PIC: ${task.picName}` :
                                                                                 'Unassigned'}
                                                                            </span>

                                                                            {!isReturned && (
                                                                                <div className="flex items-center gap-2 ml-auto">
                                                                                    <button
                                                                                        onClick={() => handleRemoveTask(taskIndex)}
                                                                                        className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                                                                                        title="Remove task"
                                                                                    >
                                                                                        <TrashIcon className="h-4 w-4" />
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                <p className="text-gray-500 dark:text-gray-400">No tasks available. Click "Add Task" to add manually.</p>
                            </div>
                        )}

                        {/* Step 4 Actions */}
                        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 inline-flex items-center"
                            >
                                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                                Back
                            </button>
                            <div className="flex gap-3">
                                {/* Save as Draft button */}
                                <button
                                    type="button"
                                    onClick={handleSaveAsDraft}
                                    disabled={selectedCount === 0 || generating}
                                    className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                >
                                    {generating ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full mr-2"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save as Draft'
                                    )}
                                </button>
                                {/* Create & Submit button */}
                                <button
                                    type="button"
                                    onClick={handleCreateAndSubmit}
                                    disabled={selectedCount === 0 || generating}
                                    className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-orange-600 rounded-lg hover:from-red-700 hover:to-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                >
                                    {generating ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircleIcon className="h-5 w-5 mr-2" />
                                            Create & Submit ({selectedCount})
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add Task Modal - Template Selection */}
            <AddTaskModal
                isOpen={showAddTaskModal}
                onClose={() => setShowAddTaskModal(false)}
                onSave={handleAddTask}
                title="Add Offboarding Task"
            />
        </div>,
        document.body
    );
}
